import React, { useEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Users, Zap, Search, Bell, MoreHorizontal, ArrowUpRight, Clock, Globe, DollarSign, CreditCard, Activity, MapPin, CheckCircle2, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
const revenueData = [{
  name: 'Jan',
  retail: 4500,
  fee: 1200
}, {
  name: 'Feb',
  retail: 5200,
  fee: 1200
}, {
  name: 'Mar',
  retail: 4800,
  fee: 1200
}, {
  name: 'Apr',
  retail: 6100,
  fee: 1500
}, {
  name: 'May',
  retail: 7500,
  fee: 1800
}, {
  name: 'Jun',
  retail: 8900,
  fee: 2100
}, {
  name: 'Jul',
  retail: 9400,
  fee: 2200
}];
const sparklineData = [{
  value: 40
}, {
  value: 35
}, {
  value: 55
}, {
  value: 45
}, {
  value: 60
}, {
  value: 50
}, {
  value: 75
}];
const activities = [{
  id: 1,
  type: 'ticket',
  text: 'New Design Ticket from California',
  time: '2 mins ago',
  status: 'urgent'
}, {
  id: 2,
  type: 'payment',
  text: 'Payment Processed from Canada',
  time: '15 mins ago',
  status: 'success'
}, {
  id: 3,
  type: 'user',
  text: 'New Reseller Onboarded: Toronto Hub',
  time: '1 hour ago',
  status: 'info'
}, {
  id: 4,
  type: 'system',
  text: 'Server Maintenance Completed',
  time: '3 hours ago',
  status: 'success'
}, {
  id: 5,
  type: 'ticket',
  text: 'SLA Update: North Star Logistics',
  time: '5 hours ago',
  status: 'warning'
}];
const profitData = [{
  name: 'Mon',
  revenue: 4000,
  fee: 1200
}, {
  name: 'Tue',
  revenue: 3000,
  fee: 1200
}, {
  name: 'Wed',
  revenue: 5000,
  fee: 1500
}, {
  name: 'Thu',
  revenue: 4500,
  fee: 1500
}, {
  name: 'Fri',
  revenue: 6000,
  fee: 1800
}, {
  name: 'Sat',
  revenue: 5500,
  fee: 1800
}, {
  name: 'Sun',
  revenue: 7000,
  fee: 2100
}];
const bpoData = [{
  name: 'Upsell Active',
  value: 65
}, {
  name: 'Standard',
  value: 35
}];
const COLORS = ['#4F46E5', '#E2E8F0'];
export function Dashboard() {
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);
  return <div className="space-y-6 max-w-[1600px] mx-auto" data-id="element-1999">
      {/* SRE Status Bar */}
      <motion.div whileHover={{
      scale: 1.005
    }} className="bg-slate-900 px-6 py-2 rounded-lg flex items-center justify-between text-[10px] font-bold text-slate-400 uppercase tracking-widest border border-slate-800 cursor-pointer group transition-all" data-id="element-2000">
        <div className="flex items-center gap-6" data-id="element-2001">
          <div className="flex items-center gap-2" data-id="element-2002">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-2003"></div>
            <span className="group-hover:text-white transition-colors" data-id="element-2004">
              AMD EPYC Node: Online
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-2005">
            <Activity size={12} className="text-indigo-400" data-id="element-2006" />
            <span className="group-hover:text-white transition-colors" data-id="element-2007">
              Ping: 14ms
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-2008">
            <Globe size={12} className="text-indigo-400" data-id="element-2009" />
            <span className="group-hover:text-white transition-colors" data-id="element-2010">
              Global CDN: Optimal
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4" data-id="element-2011">
          <div className="flex items-center gap-2" data-id="element-2012">
            <CheckCircle2 size={12} className="text-emerald-500" data-id="element-2013" />
            <span className="group-hover:text-white transition-colors" data-id="element-2014">
              Uptime: 99.99%
            </span>
          </div>
          <ChevronRight size={12} className="text-slate-600 group-hover:text-white" data-id="element-2015" />
        </div>
      </motion.div>

      {/* Header with Greeting and Search */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2016">
        <div data-id="element-2017">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-2018">
            Partner Nerve Center
          </h1>
          <p className="text-sm text-gray-500 font-medium" data-id="element-2019">
            Welcome back, Alex. Here is your global network overview.
          </p>
        </div>
        <div className="flex items-center gap-4 flex-1 max-w-md" data-id="element-2020">
          <div className="relative w-full" data-id="element-2021">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2022" />
            <input type="text" placeholder="Login as Customer..." className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition-all font-medium" data-id="element-2023" />
          </div>
        </div>
      </div>

      {/* Top Row Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-2024">
        {/* Profit Calculator Widget */}
        <div className="lg:col-span-2 bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2025">
          <div className="flex items-center justify-between mb-6" data-id="element-2026">
            <div data-id="element-2027">
              <h3 className="font-bold text-slate-900" data-id="element-2028">Profit Calculator</h3>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest" data-id="element-2029">
                Gross Revenue vs. Base Fee
              </p>
            </div>
            <div className="text-right" data-id="element-2030">
              <p className="text-lg font-bold text-emerald-600" data-id="element-2031">+$4,200.00</p>
              <p className="text-[10px] text-gray-400 font-bold uppercase" data-id="element-2032">
                Net Profit (Weekly)
              </p>
            </div>
          </div>
          <div className="h-[200px]" data-id="element-2033">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2034">
              <AreaChart data={profitData} data-id="element-2035">
                <defs data-id="element-2036">
                  <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1" data-id="element-2037">
                    <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.1} data-id="element-2038" />
                    <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} data-id="element-2039" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" data-id="element-2040" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fontSize: 10,
                fill: '#94A3B8'
              }} data-id="element-2041" />
                <YAxis hide data-id="element-2042" />
                <Tooltip data-id="element-2043" />
                <Area type="monotone" dataKey="revenue" stroke="#4F46E5" strokeWidth={2} fill="url(#colorProfit)" data-id="element-2044" />
                <Area type="monotone" dataKey="fee" stroke="#94A3B8" strokeWidth={2} fill="transparent" strokeDasharray="5 5" data-id="element-2045" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Geographic Map Heatmap */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2046">
          <h3 className="font-bold text-slate-900 mb-4" data-id="element-2047">Global Reach</h3>
          <div className="space-y-4" data-id="element-2048">
            {[{
            region: 'United States (East)',
            seats: 1250,
            color: 'bg-indigo-600'
          }, {
            region: 'Canada (Ontario)',
            seats: 860,
            color: 'bg-indigo-400'
          }, {
            region: 'United States (West)',
            seats: 420,
            color: 'bg-indigo-300'
          }, {
            region: 'Canada (BC)',
            seats: 170,
            color: 'bg-indigo-200'
          }].map(item => <div key={item.region} className="space-y-1" data-id="element-2049">
                <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest" data-id="element-2050">
                  <span className="text-gray-500" data-id="element-2051">{item.region}</span>
                  <span className="text-slate-900" data-id="element-2052">{item.seats} Seats</span>
                </div>
                <div className="w-full bg-gray-50 h-1.5 rounded-full overflow-hidden" data-id="element-2053">
                  <div className={`h-full ${item.color}`} style={{
                width: `${item.seats / 1500 * 100}%`
              }} data-id="element-2054"></div>
                </div>
              </div>)}
          </div>
          <div className="mt-6 flex items-center gap-2 text-[10px] font-bold text-indigo-600 uppercase" data-id="element-2055">
            <MapPin size={12} data-id="element-2056" />
            <span data-id="element-2057">4 Regions Active</span>
          </div>
        </div>

        {/* BPO Status Tracker */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex flex-col items-center justify-center text-center" data-id="element-2058">
          <h3 className="font-bold text-slate-900 mb-2" data-id="element-2059">BPO Upsell</h3>
          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-4" data-id="element-2060">
            $200/mo Marketing Tier
          </p>
          <div className="h-[140px] w-full" data-id="element-2061">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2062">
              <PieChart data-id="element-2063">
                <Pie data={bpoData} innerRadius={45} outerRadius={60} paddingAngle={5} dataKey="value" data-id="element-2064">
                  {bpoData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-2065" />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2" data-id="element-2066">
            <p className="text-2xl font-bold text-slate-900" data-id="element-2067">65%</p>
            <p className="text-[10px] text-gray-400 font-bold uppercase" data-id="element-2068">
              Utilization
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-2069">
        {/* Main Chart Area */}
        <div className="lg:col-span-3 bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2070">
          <div className="flex items-center justify-between mb-8" data-id="element-2071">
            <div data-id="element-2072">
              <h3 className="font-bold text-slate-900" data-id="element-2073">
                Revenue vs. Fulfillment
              </h3>
              <p className="text-xs text-gray-500 font-medium" data-id="element-2074">
                Tracking the split between retail price and NiishEngine base
                fees.
              </p>
            </div>
            <div className="flex items-center gap-4" data-id="element-2075">
              <div className="flex items-center gap-2" data-id="element-2076">
                <div className="w-3 h-3 rounded-full bg-indigo-600" data-id="element-2077"></div>
                <span className="text-xs font-bold text-gray-600" data-id="element-2078">
                  Retail Price
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2079">
                <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-2080"></div>
                <span className="text-xs font-bold text-gray-600" data-id="element-2081">
                  Base Fee
                </span>
              </div>
              <select className="text-xs font-bold border border-gray-200 rounded-lg px-3 py-1.5 bg-gray-50 outline-none" data-id="element-2082">
                <option data-id="element-2083">Last 7 Months</option>
                <option data-id="element-2084">Year to Date</option>
              </select>
            </div>
          </div>

          <div className="h-[400px] w-full relative" data-id="element-2085">
            {isLoading ? <div className="absolute inset-0 flex flex-col gap-4 animate-pulse" data-id="element-2086">
                <div className="flex-1 bg-gray-50 rounded-xl" data-id="element-2087"></div>
                <div className="h-8 flex gap-4" data-id="element-2088">
                  <div className="w-16 bg-gray-50 rounded" data-id="element-2089"></div>
                  <div className="w-16 bg-gray-50 rounded" data-id="element-2090"></div>
                  <div className="w-16 bg-gray-50 rounded" data-id="element-2091"></div>
                </div>
              </div> : <ResponsiveContainer width="100%" height="100%" data-id="element-2092">
                <AreaChart data={revenueData} data-id="element-2093">
                  <defs data-id="element-2094">
                    <linearGradient id="colorRetail" x1="0" y1="0" x2="0" y2="1" data-id="element-2095">
                      <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.1} data-id="element-2096" />
                      <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} data-id="element-2097" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" data-id="element-2098" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#94A3B8',
                fontWeight: 600
              }} dy={10} data-id="element-2099" />
                  <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#94A3B8',
                fontWeight: 600
              }} domain={['auto', 'auto']} data-id="element-2100" />
                  <Tooltip contentStyle={{
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)'
              }} data-id="element-2101" />
                  <Area type="monotone" dataKey="retail" stroke="#4F46E5" strokeWidth={3} fillOpacity={1} fill="url(#colorRetail)" name="Retail Revenue" data-id="element-2102" />
                  <Area type="monotone" dataKey="fee" stroke="#94A3B8" strokeWidth={2} fill="transparent" name="NiishEngine Fee" strokeDasharray="5 5" data-id="element-2103" />
                </AreaChart>
              </ResponsiveContainer>}
          </div>
        </div>

        {/* Right Sidebar Widget: Activity Feed */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col" data-id="element-2104">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between" data-id="element-2105">
            <h3 className="font-bold text-slate-900" data-id="element-2106">Real-time Activity</h3>
            <button className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2107">
              <MoreHorizontal size={20} data-id="element-2108" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-2109">
            {activities.map(activity => <div key={activity.id} className="flex gap-4 group cursor-pointer" data-id="element-2110">
                <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${activity.status === 'urgent' ? 'bg-red-500' : activity.status === 'success' ? 'bg-emerald-500' : activity.status === 'warning' ? 'bg-amber-500' : 'bg-indigo-500'}`} data-id="element-2111"></div>
                <div className="space-y-1" data-id="element-2112">
                  <p className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors leading-tight" data-id="element-2113">
                    {activity.text}
                  </p>
                  <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest" data-id="element-2114">
                    <Clock size={10} data-id="element-2115" />
                    {activity.time}
                  </div>
                </div>
              </div>)}
          </div>
          <div className="p-4 border-t border-gray-100" data-id="element-2116">
            <button className="w-full py-2 text-xs font-bold text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all" data-id="element-2117">
              View All Activity
            </button>
          </div>
        </div>
      </div>

      {/* Global Settings & Client Management Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2118">
        <div className="bg-slate-900 p-6 rounded-lg text-white relative overflow-hidden group cursor-pointer" data-id="element-2119">
          <div className="relative z-10" data-id="element-2120">
            <div className="flex items-center gap-3 mb-4" data-id="element-2121">
              <div className="p-2 bg-white/10 rounded-lg" data-id="element-2122">
                <Globe size={20} data-id="element-2123" />
              </div>
              <h3 className="font-bold" data-id="element-2124">Global Network Settings</h3>
            </div>
            <p className="text-sm text-slate-400 mb-6 max-w-xs" data-id="element-2125">
              Manage multi-country operations, currencies, and regional tax
              compliance.
            </p>
            <div className="flex items-center gap-2 text-xs font-bold text-indigo-400 group-hover:gap-3 transition-all" data-id="element-2126">
              Configure Infrastructure <ArrowUpRight size={14} data-id="element-2127" />
            </div>
          </div>
          <div className="absolute -right-8 -bottom-8 opacity-10 group-hover:scale-110 transition-transform duration-500" data-id="element-2128">
            <Globe size={160} data-id="element-2129" />
          </div>
        </div>

        <div className="bg-indigo-600 p-6 rounded-lg text-white relative overflow-hidden group cursor-pointer" data-id="element-2130">
          <div className="relative z-10" data-id="element-2131">
            <div className="flex items-center gap-3 mb-4" data-id="element-2132">
              <div className="p-2 bg-white/10 rounded-lg" data-id="element-2133">
                <Users size={20} data-id="element-2134" />
              </div>
              <h3 className="font-bold" data-id="element-2135">Client Management</h3>
            </div>
            <p className="text-sm text-indigo-100 mb-6 max-w-xs" data-id="element-2136">
              Jump into specific client dashboards, manage site SLAs, and handle
              support tickets.
            </p>
            <div className="flex items-center gap-2 text-xs font-bold text-white group-hover:gap-3 transition-all" data-id="element-2137">
              Open CRM Portal <ArrowUpRight size={14} data-id="element-2138" />
            </div>
          </div>
          <div className="absolute -right-8 -bottom-8 opacity-10 group-hover:scale-110 transition-transform duration-500" data-id="element-2139">
            <Users size={160} data-id="element-2140" />
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 border-dashed flex flex-col items-center justify-center text-center space-y-3" data-id="element-2141">
          <div className="p-3 bg-white rounded-full shadow-sm" data-id="element-2142">
            <CreditCard size={24} className="text-gray-300" data-id="element-2143" />
          </div>
          <div data-id="element-2144">
            <h3 className="text-sm font-bold text-gray-400" data-id="element-2145">
              Virtual Terminal
            </h3>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest" data-id="element-2146">
              Coming Soon
            </p>
          </div>
        </div>
      </div>
    </div>;
}